/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React from 'react'
import {useItemVariant} from '.'
import Link from '@salesforce/retail-react-app/app/components/link'
import {Text, useMultiStyleConfig} from '@salesforce/retail-react-app/app/components/shared/ui'
import PropTypes from 'prop-types'
import {productUrlBuilder} from '../../utils/url'
import {useLocation} from 'react-router-dom'
import {sendClickEvent} from '../../utils/gtm-utils'

/**
 * In the context of a cart product item variant, this components simply renders
 * the item's name using the cart item field `productName`. We use this field
 * rather than variant `name` field as the variant detail may not yet be
 * available upon rendering.
 */
const ItemName = ({...props}) => {
    const styles = useMultiStyleConfig('Cart')
    const location = useLocation()
    const variant = useItemVariant()
    const productName = variant?.productName || variant?.name
    const isGiftCard = variant?.c_isGiftCard || variant?.c_isEGiftCard
    const isCheckout = /\/checkout$/.test(location?.pathname)

    // GTM Modification
    const sendGTMEvent = () => {
        sendClickEvent('cart_view_product_clicked')
    }

    return (
        <Text data-testid="product-item-name" {...styles.cartItemNameText}>
            {isGiftCard || isCheckout ? (
                productName
            ) : (
                <Link
                    {...styles.cartItemNameLink}
                    to={productUrlBuilder(variant)}
                    onClick={sendGTMEvent}
                    data-testid="product-name"
                    {...props}
                >
                    {productName}
                </Link>
            )}
        </Text>
    )
}

export default ItemName
